import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ChatMessage } from '../../../services/chatSupportService';
import { splitMessageIntoLines } from '../utils/messageUtils';

interface ChatMessagesProps {
  messages: ChatMessage[];
  isTyping: boolean;
  showConnectionNotice: boolean;
  showScrollToBottom: boolean;
  onScroll: () => void;
  onScrollToBottom: () => void;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  isTyping,
  showConnectionNotice,
  showScrollToBottom,
  onScroll,
  onScrollToBottom,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [messages, isTyping]);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container) {
      onScroll();
    }
  };

  return (
    <Box
      ref={messagesContainerRef}
      className="chat-messages"
      onScroll={handleScroll}
    >
      {showConnectionNotice && (
        <Box className="chat-connection-notice">
          <Typography className="chat-connection-notice-text">
            You&apos;re connected with LottoReco support.
          </Typography>
        </Box>
      )}

      {messages.map((message) => (
        <Box
          key={message.id}
          className={`message ${message.sender}`}
        >
          <Box className="message-content">
            <Typography variant="body1" className="message-text" component="div">
              {splitMessageIntoLines(message.message).map((line, index, array) => (
                <React.Fragment key={index}>
                  {line}
                  {index < array.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
          </Box>
          <Typography variant="caption" className="message-time">
            {formatTime(message.timestamp)}
          </Typography>
        </Box>
      ))}

      {isTyping && (
        <Box className="message support typing-indicator">
          <Box className="message-content">
            <Typography variant="body2" className="typing-text">
              Support is typing...
            </Typography>
            <Box className="typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </Box>
          </Box>
        </Box>
      )}
      <div ref={messagesEndRef} />
    </Box>
  );
};

export default ChatMessages;
